/**
 * ExpedienteDetailPage
 * Pantalla principal del expediente — header + 5 nav cards + sección activa.
 * Los cards de estado general actúan como botones de navegación: al hacer click
 * en uno, se abre/cierra la sección correspondiente debajo.
 */
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { ROUTES } from '@/constants';
import { useExpedienteResumen } from '../../hooks';
import {
  ExpedienteHeader,
  EstadoGeneralCard,
  IdentitySection,
  ProfileSection,
  ContractsSection,
  OffersSection,
  ApplicationsSection,
  TrayectoriaSection,
} from '../../components';
import type { ExpedienteSection } from '../../components';

export function ExpedienteDetailPage() {
  const { directoryId } = useParams<{ directoryId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromCandidatos = searchParams.get('from') === 'candidatos';
  const goBack = () => navigate(-1);
  const { resumen, isLoading, isError } = useExpedienteResumen(directoryId);
  const [activeSection, setActiveSection] = useState<ExpedienteSection>('identity');

  const breadcrumbs = fromCandidatos
    ? [
        { label: 'Inicio', path: ROUTES.DASHBOARD },
        { label: 'Originacion', path: ROUTES.PIPELINE_MATRIX },
        { label: 'Scoring', path: ROUTES.SCORING },
        { label: 'Candidatos a Credito', path: ROUTES.CREDIT_CANDIDATOS },
        { label: resumen?.full_name ?? 'Cargando…', path: '#' },
      ]
    : [
        { label: 'Inicio', path: ROUTES.DASHBOARD },
        { label: 'Clientes', path: ROUTES.CLIENTS_LIST },
        { label: resumen?.full_name ?? 'Cargando…', path: '#' },
      ];

  if (isLoading) {
    return (
      <div>
        <PageHeader breadcrumbs={breadcrumbs} title="Expediente" onBack={goBack} />
        <Card><div className="p-6 text-muted-foreground">Cargando…</div></Card>
      </div>
    );
  }

  if (isError || !resumen || !directoryId) {
    return (
      <div>
        <PageHeader breadcrumbs={breadcrumbs} title="Expediente" onBack={goBack} />
        <Card><div className="p-6 text-red-600 dark:text-red-400">No se encontró el expediente.</div></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader breadcrumbs={breadcrumbs} title="Expediente" onBack={goBack} />

      <Card>
        <ExpedienteHeader resumen={resumen} />
        <div className="p-3">
          <EstadoGeneralCard
            resumen={resumen}
            directoryId={directoryId}
            activeSection={activeSection}
            onSelectSection={setActiveSection}
          />
        </div>
      </Card>

      <Card>
        {activeSection === 'identity' && (
          <IdentitySection directoryId={directoryId} enabled />
        )}
        {activeSection === 'profile' && (
          <ProfileSection directoryId={directoryId} />
        )}
        {activeSection === 'offers' && (
          <OffersSection directoryId={directoryId} />
        )}
        {activeSection === 'applications' && (
          <ApplicationsSection directoryId={directoryId} />
        )}
        {activeSection === 'contracts' && (
          <ContractsSection directoryId={directoryId} />
        )}
        {activeSection === 'trayectoria' && (
          <TrayectoriaSection directoryId={directoryId} />
        )}
      </Card>
    </div>
  );
}
